package net.dipgo.game

import net.dipgo.game.board.BoardPosition

interface ScoringLogic {
    fun pointsForThisBoardPositionExcludingCapturesAndKomi(boardPosition: BoardPosition): Map<Int, Int>

    /**
     * Ownership of every empty intersection that counts as a team's territory: an
     * empty region enclosed by exactly one colour maps every one of its points to
     * that colour. Empty points bordering two or more colours (dame / neutral) are
     * absent from the map. Non-empty points are absent too — a stone's owner is
     * simply its own [PointState]. Used to shade territory in the scoring view.
     */
    fun territoryOwnership(boardPosition: BoardPosition): Map<Point, PointState>
}

class ScoringLogicImp(val gameLogic: GameLogic) : ScoringLogic {
    override fun pointsForThisBoardPositionExcludingCapturesAndKomi(boardPosition: BoardPosition): Map<Int, Int> {
        val finalScore = mutableMapOf<Int, Int>()
        val pointChecked = Array(boardPosition.xSize) { BooleanArray(boardPosition.ySize) }

        for (x in 0 until boardPosition.xSize) {
            for (y in 0 until boardPosition.ySize) {
                if (!pointChecked[x][y]) {
                    val pointState: PointState = boardPosition.getStateOfPoint(x, y)
                    val groupForPoint: Set<Point> = gameLogic.groupForPoint(Point(x, y))
                    if (pointState != PointState.EMPTY) {
                        addPointsToCurrentScore(finalScore, pointState, groupForPoint)
                    } else {
                        val uniqueAdjacentPointStates: Set<PointState> =
                            uniquePointStatesEnclosingArea(groupForPoint, boardPosition)
                        if (uniqueAdjacentPointStates.size == 1) {
                            val adjacentPointState: PointState = uniqueAdjacentPointStates.first()
                            addPointsToCurrentScore(finalScore, adjacentPointState, groupForPoint)
                        }
                    }
                    groupForPoint.forEach { pointChecked[it.xCoord][it.yCoord] = true }
                }
            }
        }
        return finalScore
    }

    override fun territoryOwnership(boardPosition: BoardPosition): Map<Point, PointState> {
        val ownership = mutableMapOf<Point, PointState>()
        val pointChecked = Array(boardPosition.xSize) { BooleanArray(boardPosition.ySize) }

        for (x in 0 until boardPosition.xSize) {
            for (y in 0 until boardPosition.ySize) {
                if (!pointChecked[x][y]) {
                    val groupForPoint: Set<Point> = gameLogic.groupForPoint(Point(x, y))
                    if (boardPosition.getStateOfPoint(x, y) == PointState.EMPTY) {
                        val uniqueAdjacentPointStates: Set<PointState> =
                            uniquePointStatesEnclosingArea(groupForPoint, boardPosition)
                        if (uniqueAdjacentPointStates.size == 1) {
                            val owner: PointState = uniqueAdjacentPointStates.first()
                            groupForPoint.forEach { ownership[it] = owner }
                        }
                    }
                    groupForPoint.forEach { pointChecked[it.xCoord][it.yCoord] = true }
                }
            }
        }
        return ownership
    }

    private fun uniquePointStatesEnclosingArea(
        groupForPoint: Set<Point>,
        boardPosition: BoardPosition
    ): Set<PointState> {
        return groupForPoint.flatMap { group ->
            gameLogic.getAdjacentPoints(Point(group.xCoord, group.yCoord)).mapNotNull { adjacentPoint ->
                boardPosition.getStateOfPoint(adjacentPoint.xCoord, adjacentPoint.yCoord)
                    .takeIf { it != PointState.EMPTY }
            }
        }.toSet()
    }

    private fun addPointsToCurrentScore(
        finalScore: MutableMap<Int, Int>,
        adjacentPointState: PointState,
        groupForPoint: Set<Point>
    ) {
        val currentScore = finalScore[adjacentPointState.ordinal] ?: 0
        finalScore[adjacentPointState.ordinal] = currentScore + groupForPoint.size
    }

}